import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandSymbol from '../ui/BrandSymbol';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { apiService } from '../../utils/api';
import { ResponsiveContainer, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';
import type { UnifiedContact } from '../../services/unifiedContactService';
import { unifiedContactService } from '../../services/unifiedContactService';

interface ExecutorInvitation {
  id: string;
  email: string;
  name?: string;
  status: 'pending' | 'accepted';
  invitedAt: Date;
}

interface InvitationFormData {
  firstName: string;
  lastName: string;
  email: string;
}

const Frame158: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress, isLoading } = useSessionPersistence();
  const [selectedExecutors, setSelectedExecutors] = useState<string[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [invitationForm, setInvitationForm] = useState<InvitationFormData>({
    firstName: '',
    lastName: '',
    email: ''
  });

  // Load saved executors from session
  const [invitedExecutors, setInvitedExecutors] = useState<ExecutorInvitation[]>([]);
  const [hasReconciled, setHasReconciled] = useState(false);
  const [allContacts, setAllContacts] = useState<UnifiedContact[]>([]);

  // Load saved data on component mount (simplified to prevent infinite loops)
  useEffect(() => {
    // Reset reconciliation flag on mount
    setHasReconciled(false);
    // Load all contacts from unified system
    loadAllContacts();
  }, []); // Only run once on mount

  const loadAllContacts = async () => {
    try {
      const contacts = await unifiedContactService.getAllContacts();
      setAllContacts(contacts);
      console.log(`[Frame158] Loaded ${contacts.length} unified contacts`);
    } catch (error) {
      console.error('[Frame158] Error loading contacts:', error);
    }
  };

  // Load data when session is available
  useEffect(() => {
    console.log('Frame158: useEffect triggered - isLoading:', isLoading, 'hasReconciled:', hasReconciled);
    console.log('Frame158: Session data:', session.taskProgress);
    
    // Only load data when session is available and not already reconciled
    if (!isLoading && session.taskProgress?.executors && !hasReconciled) {
      console.log('Frame158: Loading saved executor data from session');
      const savedData = session.taskProgress.executors;
      console.log('Frame158: Saved data structure:', savedData);
      
      // The data is stored in the answers object by updateTaskProgress
      if (savedData.answers?.selectedExecutors) {
        console.log('Frame158: Loading selected executors:', savedData.answers.selectedExecutors);
        setSelectedExecutors(savedData.answers.selectedExecutors);
      }
      if (savedData.answers?.invitedExecutors) {
        console.log('Frame158: Loading invited executors:', savedData.answers.invitedExecutors);
        // Fix date serialization issue - convert string dates back to Date objects
        const fixedInvitedExecutors = savedData.answers.invitedExecutors.map((executor: unknown) => ({
          ...executor,
          invitedAt: executor.invitedAt ? new Date(executor.invitedAt) : new Date()
        }));
        setInvitedExecutors(fixedInvitedExecutors);
      }
      
      setHasReconciled(true);
      console.log('Frame158: Data loading completed');
    } else {
      console.log('Frame158: Skipping data load - isLoading:', isLoading, 'hasExecutors:', !!session.taskProgress?.executors, 'hasReconciled:', hasReconciled);
    }
  }, [isLoading, session.taskProgress?.executors, hasReconciled]);

  // Save data to session whenever selections change
  const saveToSession = (selected: string[], invited: ExecutorInvitation[]) => {
    console.log('Frame158: Saving to session - selected:', selected, 'invited:', invited);
    updateTaskProgress('executors', {
      selectedExecutors: selected,
      invitedExecutors: invited
    });
  };

  const handleBack = () => {
    navigate('/frame-140');
  };

  const handleNext = () => {
    // Navigate to next step in executor workflow
    navigate('/frame-163');
  };

  const handleExecutorSelect = (executorId: string) => {
    const newSelected = selectedExecutors.includes(executorId) 
      ? selectedExecutors.filter(e => e !== executorId)
      : [...selectedExecutors, executorId];
    
    setSelectedExecutors(newSelected);
    saveToSession(newSelected, invitedExecutors);
  };

  const handleInviteByEmail = () => {
    setShowInvitationModal(true);
  };

  const handleInvitationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Create contact via unified contact service
      const newContact = await unifiedContactService.createContact({
        firstName: invitationForm.firstName.trim(),
        lastName: invitationForm.lastName.trim(),
        email: invitationForm.email.trim(),
        relationship: 'executor',
        source: 'executor_invite',
        notes: 'Invited as executor'
      });

      // Reload contacts to include the new one
      await loadAllContacts();

      // Auto-select the newly created contact
      const newSelected = [...selectedExecutors, newContact.id];
      setSelectedExecutors(newSelected);
      saveToSession(newSelected, invitedExecutors);

      // Transmit data to backend
      try {
        const transmissionSuccess = await apiService.transmitExecutorData({
          selectedExecutors: newSelected,
          invitedExecutors,
          userId: session.email || session.id,
          sessionId: session.id,
          accountId: session.accountId
        });

        if (transmissionSuccess) {
          console.log('✅ Executor data transmitted to backend successfully');
        } else {
          console.warn('âš ï¸ Failed to transmit executor data to backend');
        }
      } catch (error) {
        console.error('âŒ Error transmitting executor data:', error);
      }

      // Reset form and close modal
      setInvitationForm({ firstName: '', lastName: '', email: '' });
      setShowInvitationModal(false);
      setIsDropdownOpen(false);
    } catch (error: unknown) {
      if (error.code === 'DUPLICATE_CONTACT') {
        alert(`A contact with email ${invitationForm.email} already exists. Please select them from the list.`);
      } else {
        alert('Failed to create executor contact. Please try again.');
      }
      console.error('[Frame158] Error creating executor contact:', error);
    }
  };

  const handleFormChange = (field: keyof InvitationFormData, value: string) => {
    setInvitationForm(prev => ({ ...prev, [field]: value }));
  };

  const getDropdownContent = () => {
    if (allContacts.length === 0) {
      return (
        <div className="absolute top-full left-0 w-full mt-1 bg-white rounded border border-neutral-200 shadow-lg z-10">
          <div className="p-4 text-center">
            <div className="text-stone-500 text-sm">No contacts available</div>
            <button
              onClick={handleInviteByEmail}
              className="mt-2 text-cyan-700 text-sm underline hover:text-cyan-800"
            >
              Add a contact by email
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="absolute top-full left-0 w-full mt-1 bg-white rounded border border-neutral-200 shadow-lg z-10 max-h-96 overflow-y-auto">
        {allContacts.map((contact) => (
          <div
            key={contact.id}
            className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
            onClick={() => handleExecutorSelect(contact.id)}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-stone-800 text-base font-medium">
                  {contact.firstName} {contact.lastName}
                </div>
                <div className="text-stone-500 text-sm">{contact.email}</div>
                {contact.relationship && (
                  <div className="text-stone-400 text-xs capitalize">
                    {contact.relationship}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {contact.source === 'executor_invite' && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    Invited
                  </span>
                )}
                {selectedExecutors.includes(contact.id) && (
                  <div className="w-4 h-4 bg-cyan-700 rounded-full flex items-center justify-center">
                    <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        <div className="p-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleInviteByEmail}
            className="text-cyan-700 text-sm underline hover:text-cyan-800"
          >
            + Add another contact
          </button>
        </div>
      </div>
    );
  };

  return (
    <ResponsiveContainer background="stone">
      {/* Centered Brand Logo */}
      <div className="absolute top-8 left-1/2 transform -translate-x-1/2 z-10">
        <BrandSymbol size="3xl" showText variant="dark" />
    </div>

      <ResponsiveContent className="pt-32">
        {/* Header with title */}
        <div className="max-w-4xl mx-auto mb-8">
          <h1 className="text-cyan-900 text-2xl sm:text-3xl font-semibold font-['Test_Domaine_Display'] leading-tight">
            Choose executor(s) for your estate
          </h1>
        </div>
        
        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          {/* Description */}
          <div className="mb-8">
            <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
              Choosing one or more "executors" is one of the most important first steps in planning your estate. 
              An executor will be responsible for administering your affairs after your death. All executors 
              will be notified via email and invited to learn more about the role.
            </p>
            </div>
            
          {/* Executor Selection Dropdown */}
          <div className="mb-4 relative">
            <div 
              className="w-full h-9 bg-white rounded border border-neutral-200 cursor-pointer flex items-center px-3 pr-8"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <div className="flex-1 text-stone-800 text-base font-medium font-['Work_Sans'] leading-none">
                {selectedExecutors.length > 0 ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    {allContacts
                      .filter(contact => selectedExecutors.includes(contact.id))
                      .map((contact, index) => (
                        <div key={contact.id} className="flex items-center gap-1">
                          <span className="truncate">{`${contact.firstName} ${contact.lastName}`.trim() || contact.email}</span>
                          <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {index < selectedExecutors.length - 1 && <span>,</span>}
                        </div>
                      ))}
                  </div>
                ) : (
                  'Choose one or more people'
                )}
              </div>
              
              {/* Dropdown arrow */}
              <div className="flex-shrink-0">
                <svg 
                  className={`w-4 h-4 text-stone-600 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
              </div>
            </div>

            {/* Dropdown options */}
            {isDropdownOpen && getDropdownContent()}
          </div>

          {/* Or invite by email */}
          <div className="mb-8">
            <span className="text-stone-500 text-base font-normal font-['Work_Sans']">or </span>
            <button 
              onClick={handleInviteByEmail}
              className="text-stone-500 text-base font-normal font-['Work_Sans'] underline hover:text-stone-700"
            >
              invite someone by email
            </button>
    </div>

          {/* Free resource banner */}
          <div className="bg-orange-100 rounded p-4 flex items-center gap-3">
            <div className="w-3.5 h-3.5 bg-orange-400 rounded-sm flex-shrink-0"></div>
            <div>
              <span className="text-orange-700 text-sm font-medium font-['Work_Sans'] leading-none">Free Resource: </span>
              <button 
                onClick={() => {
                  const executorArticle = articles.find(a => a.id === 'executors');
                  if (executorArticle) setSelectedArticle(executorArticle);
                }}
                className="text-orange-700 text-sm font-medium font-['Work_Sans'] underline leading-none hover:text-orange-800"
              >
                What is an executor? →
              </button>
            </div>
          </div>

        </div>
      </ResponsiveContent>

      {/* Responsive Footer */}
      <ResponsiveFooter
        title="Choose executors"
        subtitle="Approximately 3 minutes"
        progressPercent={8}
        onBack={handleBack}
        onNext={selectedExecutors.length > 0 ? handleNext : undefined}
        nextDisabled={selectedExecutors.length === 0}
        backText="← Back"
        nextText="Next →"
      />

      {/* Invitation Modal */}
      {showInvitationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-stone-800">Who do you want to invite?</h2>
                <button 
                onClick={() => setShowInvitationModal(false)}
                className="text-stone-500 hover:text-stone-700"
                >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                </button>
              </div>

            <form onSubmit={handleInvitationSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={invitationForm.firstName}
                  onChange={(e) => handleFormChange('firstName', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Last Name</label>
                      <input
                  type="text"
                  value={invitationForm.lastName}
                  onChange={(e) => handleFormChange('lastName', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Email Address</label>
                  <input
                  type="email"
                  value={invitationForm.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent"
                  required
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowInvitationModal(false)}
                  className="flex-1 px-4 py-2 border border-neutral-300 text-stone-700 rounded-md hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-cyan-700 text-white rounded-md hover:bg-cyan-800"
                >
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </ResponsiveContainer>
  );
};

export default Frame158; 